import { accumulatedTranslate, elementBounds } from './svgElementGeometry';
import { buildGlazingClipMarkup } from './glazingClipMarkup';

export interface GlazingSlot {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Clip-path markup in objectBoundingBox space (0–1 within the slot image). */
  clipMarkup?: string;
}

const GLASS_PAINT = /url\(#glass(?:fill)?\)/i;

function rectSlot(element: Element, root: Element, id: string): GlazingSlot | null {
  const width = Number(element.getAttribute('width'));
  const height = Number(element.getAttribute('height'));
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  const origin = accumulatedTranslate(element, root);
  return {
    id,
    x: origin.x + Number(element.getAttribute('x') ?? 0),
    y: origin.y + Number(element.getAttribute('y') ?? 0),
    width,
    height,
  };
}

function slotSort(left: GlazingSlot, right: GlazingSlot): number {
  const leftIndex = Number(left.id.replace(/\D+/g, ''));
  const rightIndex = Number(right.id.replace(/\D+/g, ''));
  if (Number.isFinite(leftIndex) && Number.isFinite(rightIndex) && leftIndex !== rightIndex) {
    return leftIndex - rightIndex;
  }
  return left.y - right.y || left.x - right.x;
}

function glazingGuideElements(root: Element): Element[] {
  const group = root.querySelector('#glazing-guides, [id="glazing-guides"]');
  if (!group) return [];
  const named = Array.from(group.querySelectorAll('.glazing-guide, [id^="glazing-guide-"]'));
  return named.length > 0 ? named : Array.from(group.querySelectorAll('rect'));
}

function guideSlot(element: Element, root: Element, id: string): GlazingSlot | null {
  if (element.tagName.toLowerCase() === 'rect') {
    return rectSlot(element, root, id);
  }
  const bounds = elementBounds(element, root);
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
    return null;
  }
  return {
    id,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    clipMarkup: buildGlazingClipMarkup(element, root, bounds),
  };
}

function extractFromGlazingGuides(root: Element): GlazingSlot[] {
  const slots: GlazingSlot[] = [];
  glazingGuideElements(root).forEach((element, index) => {
    const slotId =
      element.getAttribute('data-slot-id') ||
      (element.id.startsWith('glass-slot-') ? element.id : `glass-slot-${index + 1}`);
    const slot = guideSlot(element, root, slotId);
    if (slot) slots.push(slot);
  });
  return slots.sort(slotSort);
}

/**
 * Read glazing apertures from `#glazing-guides`. Rect guides clip as rects;
 * path/polygon/circle guides also expose `clipMarkup` for shaped clipping.
 * Older assets may still expose `#glass-slot-N` or `url(#glass)` rects.
 * Guides are metadata only; they are not punched or recoloured here.
 */
export function extractGlazingSlots(root: Element): GlazingSlot[] {
  const fromGuides = extractFromGlazingGuides(root);
  if (fromGuides.length > 0) return fromGuides;

  const namedSlots = Array.from(root.querySelectorAll('[id^="glass-slot-"]'));
  const source =
    namedSlots.length > 0
      ? namedSlots
      : Array.from(root.querySelectorAll('rect')).filter((rect) =>
          GLASS_PAINT.test(rect.getAttribute('fill') ?? '')
        );

  const slots: GlazingSlot[] = [];
  source.forEach((element, index) => {
    const id = element.id?.startsWith('glass-slot-') ? element.id : `glass-slot-${index + 1}`;
    const slot = rectSlot(element, root, id);
    if (slot) slots.push(slot);
  });
  return slots.sort(slotSort);
}

/** @deprecated Use extractGlazingSlots. */
export const extractAndPunchGlazingSlots = extractGlazingSlots;
